import { useState, useEffect } from "react";
import { getVendors, createVendor, updateVendor, deleteVendor } from "../lib/api";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Phone, Mail, MapPin, User, Loader2, Calendar } from "lucide-react";

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    phone: "",
    email: "",
    address: "",
    delivery_days: [],
  });

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await getVendors();
      setVendors(response.data);
    } catch (error) {
      toast.error("Failed to load vendors");
    } finally {
      setIsLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedVendor(null);
    setFormData({ name: "", contact: "", phone: "", email: "", address: "", delivery_days: [] });
    setIsDialogOpen(true);
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      name: vendor.name || "",
      contact: vendor.contact || "",
      phone: vendor.phone || "",
      email: vendor.email || "",
      address: vendor.address || "",
      delivery_days: vendor.delivery_days || [],
    });
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (vendor) => {
    setSelectedVendor(vendor);
    setIsDeleteOpen(true);
  };

  const toggleDeliveryDay = (day) => {
    setFormData(prev => ({
      ...prev,
      delivery_days: prev.delivery_days.includes(day)
        ? prev.delivery_days.filter(d => d !== day)
        : [...prev.delivery_days, day]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    
    setIsSaving(true);
    try {
      if (selectedVendor) {
        await updateVendor(selectedVendor.id, formData);
        toast.success("Vendor updated");
      } else {
        await createVendor(formData);
        toast.success("Vendor created");
      }
      setIsDialogOpen(false);
      loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save vendor");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteVendor(selectedVendor.id);
      toast.success("Vendor deleted");
      setIsDeleteOpen(false);
      loadVendors();
    } catch (error) {
      toast.error("Failed to delete vendor");
    }
  };

  return (
    <AppLayout title="Vendors" showBack>
      <div className="p-4 md:p-6 pb-24 max-w-2xl mx-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : vendors.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-stone-500 mb-4">No vendors yet</p>
            <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-first-vendor">
              <Plus className="w-4 h-4 mr-2" /> Add Your First Vendor
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {vendors.map((vendor, index) => (
              <Card 
                key={vendor.id} 
                className={`card-hover animate-in stagger-${(index % 4) + 1}`}
                data-testid={`vendor-card-${vendor.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-stone-900 truncate">{vendor.name}</h3>
                      <div className="mt-2 space-y-1 text-sm text-stone-500">
                        {vendor.contact && (
                          <div className="flex items-center gap-2">
                            <User className="w-3.5 h-3.5" />
                            <span className="truncate">{vendor.contact}</span>
                          </div>
                        )}
                        {vendor.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5" />
                            <span>{vendor.phone}</span>
                          </div>
                        )}
                        {vendor.email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5" />
                            <span className="truncate">{vendor.email}</span>
                          </div>
                        )}
                        {vendor.address && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-3.5 h-3.5" />
                            <span className="truncate">{vendor.address}</span>
                          </div>
                        )}
                        {vendor.delivery_days && vendor.delivery_days.length > 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <Calendar className="w-3.5 h-3.5 text-kale-600" />
                            <span className="text-kale-700 font-medium">
                              {vendor.delivery_days.map(d => d.slice(0, 3)).join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(vendor)}
                        className="h-9 w-9 text-stone-500 hover:text-kale-700"
                        data-testid={`edit-vendor-${vendor.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(vendor)}
                        className="h-9 w-9 text-stone-500 hover:text-red-600"
                        data-testid={`delete-vendor-${vendor.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Sticky Add Button */}
      <div className="sticky-bottom">
        <Button onClick={openCreateDialog} className="w-full btn-primary" data-testid="add-vendor-button">
          <Plus className="w-4 h-4 mr-2" /> Add Vendor
        </Button>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedVendor ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
            <DialogDescription>
              {selectedVendor ? "Update vendor information below." : "Fill in the vendor details below."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Vendor Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Fresh Farms Co."
                className="input-mobile"
                data-testid="vendor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact">Contact Person</Label>
              <Input
                id="contact"
                value={formData.contact}
                onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                placeholder="e.g., John Smith"
                className="input-mobile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="e.g., 555-0123"
                className="input-mobile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="e.g., orders@vendor.com"
                className="input-mobile"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="e.g., 123 Main St, City"
                className="input-mobile"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Preferred Delivery Days
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {DAYS_OF_WEEK.map((day) => (
                  <div
                    key={day}
                    onClick={() => toggleDeliveryDay(day)}
                    className={`p-2 rounded-lg border text-sm cursor-pointer transition-colors ${
                      formData.delivery_days.includes(day)
                        ? 'bg-kale-100 border-kale-500 text-kale-800'
                        : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300'
                    }`}
                    data-testid={`delivery-day-${day.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        formData.delivery_days.includes(day)
                          ? 'bg-kale-600 border-kale-600'
                          : 'border-stone-300'
                      }`}>
                        {formData.delivery_days.includes(day) && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      {day.slice(0, 3)}
                    </div>
                  </div>
                ))}
              </div>
              {formData.delivery_days.length > 0 && (
                <p className="text-xs text-stone-500">
                  Selected: {formData.delivery_days.join(', ')}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 h-11">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" disabled={isSaving} data-testid="save-vendor-button">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedVendor?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
